import { useMemo, useState, useRef, useCallback, useEffect, type CSSProperties } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { CellClickedEvent, ColDef, GridApi, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { Chip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  RootState,
  useLazyGetAuditLogsQuery,
  useTenantDetails,
  type AuditLogApiFilter,
  type AuditLogItem,
} from '@circuitry-ai/doc-data';
import { convertDateFormat } from '../utils/commonutils';
import { useTheme } from '../context/ThemeContext';
import { toTitleCase } from '../formgenie/formgenie-utils';
import CtySpinner from '../spinner/CtySpinner';
import { CtyButton } from '../button/CtyButton';
import { getAuditStatusChipClass, getAuditTypeChipClass } from './auditLogsUtils';
import {
  buildAuditLogApiSortingFromGrid,
  buildAuditLogsHistoryRequest,
} from './auditLogsApiUtils';
import { AuditLogDetailDrawer } from './auditLogDetailDrawer';

const DEFAULT_DATE_TIME_FORMAT = 'MM-dd-yyyy HH:mm:ss';
const DEFAULT_SERVER_PAGE_SIZE = 50;
const DEFAULT_GRID_PAGE_SIZE = 20;
const DEFAULT_SORT_FIELD = 'executed_at';
const DEFAULT_SORT_ORDER = 'desc' as const;

function formatAuditLogDisplayValue(value: string | null | undefined): string {
  if (!value) return '--';
  return toTitleCase(value);
}

function useDebouncedCallback<T extends (...args: never[]) => void>(fn: T, delay = 700) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const callback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) globalThis.clearTimeout(timeoutRef.current);
      timeoutRef.current = globalThis.setTimeout(() => fn(...args), delay);
    },
    [fn, delay]
  );
  return callback as T;
}

function AuditTypeChipCellRenderer(params: ICellRendererParams<AuditLogItem>) {
  const { t } = useTranslation();
  const value = params.value as string | undefined;
  if (value) {
    return (
      <div className="flex items-center h-full">
        <Chip
          size="sm"
          variant="flat"
          classNames={{
            base: `border capitalize ${getAuditTypeChipClass(value)}`,
            content: 'capitalize text-xs font-medium',
          }}
        >
          {t(value)}
        </Chip>
      </div>
    );
  }
  return null;
}

function AuditStatusChipCellRenderer(params: ICellRendererParams<AuditLogItem>) {
  const { t } = useTranslation();
  const value = params.value as string | undefined;
  if (value) {
    return (
      <div className="flex items-center h-full">
        <Chip
          size="sm"
          variant="flat"
          classNames={{
            base: `border capitalize ${getAuditStatusChipClass(value)}`,
            content: 'capitalize text-xs font-medium',
          }}
        >
          {t(value)}
        </Chip>
      </div>
    );
  }
  return null;
}

export interface AuditLogsAgGridWrapperProps {
  /** Static filters always applied (e.g. module/sub_module presets). */
  staticFilters?: AuditLogApiFilter[];
  dateTimeFormat?: string;
  perPage?: number;
  paginationPageSize?: number;
  className?: string;
  containerStyle?: CSSProperties;
}

function AuditLogsAgGridWrapper({
  staticFilters: staticFiltersProp = [],
  dateTimeFormat: dateTimeFormatProp,
  perPage = DEFAULT_SERVER_PAGE_SIZE,
  paginationPageSize = DEFAULT_GRID_PAGE_SIZE,
  className,
  containerStyle,
}: Readonly<AuditLogsAgGridWrapperProps>) {
  const { t } = useTranslation();
  const { details: user } = useSelector((state: RootState) => state.auth.user);
  const { isDark } = useTheme();
  const [rows, setRows] = useState<AuditLogItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const fetchInProgressRef = useRef(false);
  const pendingFetchRef = useRef(false);
  const lastFetchedServerPageRef = useRef(0);
  const lastFetchArgsRef = useRef('');
  const lastFetchParamsRef = useRef<{ page: number } | null>(null);
  const lastPaginationPageRef = useRef(-1);
  const isSortOrFilterChangingRef = useRef(false);
  const isResettingRef = useRef(false);
  const gridApiRef = useRef<GridApi | null>(null);
  const sortRef = useRef<{ field: string | null; order: 'asc' | 'desc' | null }>({
    field: DEFAULT_SORT_FIELD,
    order: DEFAULT_SORT_ORDER,
  });

  const staticFiltersKey = useMemo(() => JSON.stringify(staticFiltersProp), [staticFiltersProp]);
  const staticFiltersRef = useRef(staticFiltersProp);
  staticFiltersRef.current = staticFiltersProp;

  const userDateTimeFormat = user?.user_metadata?.preference?.dateTimeFormat;
  const { tenantDetails } = useTenantDetails({ enabled: !dateTimeFormatProp && !userDateTimeFormat });
  const tenantDateTimeFormat =
    ((tenantDetails?.meta_data as Record<string, unknown> | undefined)?.preference as Record<string, unknown> | undefined)?.dateTimeFormat as string | undefined;
  const resolvedDateTimeFormat =
    dateTimeFormatProp || userDateTimeFormat || tenantDateTimeFormat || DEFAULT_DATE_TIME_FORMAT;

  const [getAuditLogs, { isLoading, error }] = useLazyGetAuditLogsQuery();

  const resetFetchState = useCallback(() => {
    fetchInProgressRef.current = false;
    pendingFetchRef.current = false;
    lastFetchedServerPageRef.current = 0;
    lastFetchArgsRef.current = '';
    lastFetchParamsRef.current = null;
    lastPaginationPageRef.current = -1;
  }, []);

  const fetchAuditLogs = useCallback(
    async (serverPage = 1) => {
      if (fetchInProgressRef.current) {
        pendingFetchRef.current = true;
        lastFetchParamsRef.current = { page: serverPage };
        return;
      }

      const sorting =
        sortRef.current.field && sortRef.current.order
          ? { field: sortRef.current.field, order: sortRef.current.order }
          : undefined;

      const requestBody = buildAuditLogsHistoryRequest({
        gridApi: gridApiRef.current,
        staticFilters: staticFiltersRef.current,
        page: serverPage,
        perPage,
        sorting,
      });

      const requestKey = JSON.stringify(requestBody);
      if (lastFetchArgsRef.current === requestKey) return;

      lastFetchArgsRef.current = requestKey;
      lastFetchParamsRef.current = { page: serverPage };

      try {
        fetchInProgressRef.current = true;
        const response = await getAuditLogs(requestBody).unwrap();
        setTotalCount(response?.total_count ?? 0);
        setRows((prevRows) =>
          serverPage === 1 ? (response?.data ?? []) : [...prevRows, ...(response?.data ?? [])]
        );
        lastFetchedServerPageRef.current = serverPage;
        setHasLoadedOnce(true);
      } catch {
        lastFetchArgsRef.current = '';
      } finally {
        fetchInProgressRef.current = false;
        if (pendingFetchRef.current) {
          pendingFetchRef.current = false;
          const nextPage = lastFetchParamsRef.current?.page ?? 1;
          fetchAuditLogs(nextPage);
        }
      }
    },
    [getAuditLogs, perPage]
  );

  const fetchAuditLogsRef = useRef(fetchAuditLogs);
  fetchAuditLogsRef.current = fetchAuditLogs;

  useEffect(() => {
    setRows([]);
    setTotalCount(0);
    setHasLoadedOnce(false);
    resetFetchState();
    fetchAuditLogsRef.current(1);
  }, [staticFiltersKey, perPage, resetFetchState]);

  const debouncedRefetchForFilters = useDebouncedCallback(() => {
    lastPaginationPageRef.current = -1;
    isSortOrFilterChangingRef.current = true;
    lastFetchArgsRef.current = '';
    fetchAuditLogs(1).finally(() => {
      globalThis.setTimeout(() => {
        isSortOrFilterChangingRef.current = false;
      }, 100);
    });
  }, 700);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      suppressHeaderFilterButton: true,
      suppressFloatingFilterButton: true,
      sortable: true,
      resizable: true,
    }),
    []
  );

  const hasModuleStaticFilter = staticFiltersProp.some((f) => 'module' in f);
  const hasSubModuleStaticFilter = staticFiltersProp.some((f) => 'sub_module' in f);

  const columnDefs = useMemo<ColDef<AuditLogItem>[]>(
    () => [
      {
        headerName: t('Type'),
        field: 'audit_type',
        width: 110,
        cellRenderer: AuditTypeChipCellRenderer
        // filter: false,
        // floatingFilter: false,
      },
      ...(!hasModuleStaticFilter
        ? [
            {
              headerName: t('Module'),
              field: 'module' as const,
              width: 150,
              valueFormatter: (params: { value: unknown }) =>
                formatAuditLogDisplayValue(params.value as string | null),
            },
          ]
        : []),
      ...(!hasSubModuleStaticFilter
        ? [
            {
              headerName: t('Sub Module'),
              field: 'sub_module' as const,
              width: 150,
              valueFormatter: (params: { value: unknown }) =>
                formatAuditLogDisplayValue(params.value as string | null),
            },
          ]
        : []),
      {
        headerName: t('Resource'),
        field: 'resource_id',
        width: 150,
        valueFormatter: (params) => params.value || '--',
      },
      {
        headerName: t('Resource Name'),
        field: 'resource_name',
        width: 150,
        valueFormatter: (params) => formatAuditLogDisplayValue(params.value as string | null),
      },
      {
        headerName: t('Description'),
        field: 'description',
        width: 250,
      },
      {
        headerName: t('Actor'),
        field: 'executed_by',
        width: 180,
      },
      {
        headerName: t('Date'),
        field: 'executed_at',
        width: 180,
        filter: 'agDateColumnFilter',
        valueFormatter: (params) =>
          params.value
            ? convertDateFormat(params.value as string, resolvedDateTimeFormat) || String(params.value)
            : '--',
      },
      {
        headerName: t('Status'),
        field: 'status',
        width: 110,
        cellRenderer: AuditStatusChipCellRenderer,
        filter: false,
        floatingFilter: false,
      },
    ],
    [resolvedDateTimeFormat, t, hasModuleStaticFilter, hasSubModuleStaticFilter]
  );

  const onSortChanged = useCallback(
    (event: { api?: GridApi }) => {
      if (isResettingRef.current) return;
      const api = event.api ?? gridApiRef.current;
      if (!api) return;

      isSortOrFilterChangingRef.current = true;
      const state = api.getColumnState().find((column) => !!column.sort);
      if (!state?.colId || !state.sort) {
        sortRef.current = { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_ORDER };
      } else {
        const sorting = buildAuditLogApiSortingFromGrid(api);
        sortRef.current = sorting
          ? { field: sorting.field, order: sorting.order }
          : { field: state.colId, order: state.sort as 'asc' | 'desc' };
      }
      debouncedRefetchForFilters();
    },
    [debouncedRefetchForFilters]
  );

  const onFilterChanged = useCallback(() => {
    if (isResettingRef.current) return;
    isSortOrFilterChangingRef.current = true;
    debouncedRefetchForFilters();
  }, [debouncedRefetchForFilters]);

  const onPaginationChanged = useCallback(
    (params: {
      api?: {
        paginationGetCurrentPage: () => number;
        paginationGetTotalPages: () => number;
        paginationGetRowCount: () => number;
      };
    }) => {
      if (fetchInProgressRef.current || isSortOrFilterChangingRef.current) return;
      const api = params?.api;
      if (!api) return;

      const currentGridPage = api.paginationGetCurrentPage();
      const totalGridPages = api.paginationGetTotalPages();
      const loadedRowCount = api.paginationGetRowCount();

      if (currentGridPage + 1 === totalGridPages && loadedRowCount < totalCount) {
        const nextServerPage = lastFetchedServerPageRef.current + 1;
        if (lastPaginationPageRef.current === nextServerPage) return;
        lastPaginationPageRef.current = nextServerPage;
        lastFetchArgsRef.current = '';
        fetchAuditLogs(nextServerPage);
      } else {
        lastPaginationPageRef.current = -1;
      }
    },
    [fetchAuditLogs, totalCount]
  );

  const onGridReady = useCallback((params: { api: GridApi }) => {
    gridApiRef.current = params.api;
    params.api.applyColumnState({
      state: [{ colId: 'executed_at', sort: DEFAULT_SORT_ORDER, sortIndex: 0 }],
      defaultState: { sort: null },
    });
    sortRef.current = { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_ORDER };
    params.api.sizeColumnsToFit();
  }, []);

  const onFirstDataRendered = useCallback((params: { api: GridApi }) => {
    params.api.sizeColumnsToFit();
  }, []);

  useEffect(() => {
    const handleResize = () => gridApiRef.current?.sizeColumnsToFit();
    globalThis.window?.addEventListener('resize', handleResize);
    return () => globalThis.window?.removeEventListener('resize', handleResize);
  }, []);

  const openAuditDetail = useCallback((data: AuditLogItem | undefined, api?: GridApi) => {
    const auditId = data?.audit_id;
    if (!auditId) return;
    api?.getRowNode(auditId)?.setSelected(true, true);
    setSelectedAuditId(auditId);
    setIsDetailOpen(true);
  }, []);

  const onCellClicked = useCallback(
    (event: CellClickedEvent<AuditLogItem>) => {
      openAuditDetail(event.data, event.api);
    },
    [openAuditDetail]
  );

  const handleDetailClose = useCallback(() => {
    setIsDetailOpen(false);
    gridApiRef.current?.deselectAll();
    gridApiRef.current?.clearFocusedCell();
    globalThis.setTimeout(() => {
      setSelectedAuditId(null);
    }, 220);
  }, []);

  const resetFilters = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;

    const filterModel = api.getFilterModel();
    const hasFilters = filterModel && Object.keys(filterModel).length > 0;
    const hasSorts = api.getColumnState?.()?.some((c) => c.sort && (c.colId !== DEFAULT_SORT_FIELD || c.sort !== DEFAULT_SORT_ORDER));

    if (!hasFilters && !hasSorts) return;

    isResettingRef.current = true;

    const allColumns = api.getAllGridColumns?.() || [];
    allColumns.forEach((col) => {
      const colId = (col as any).getColId?.();
      if (colId) api.destroyFilter(colId);
    });
    api.setFilterModel(null);
    const colState = api.getColumnState?.();
    if (colState) {
      api.applyColumnState?.({ state: colState.map((c) => ({ ...c, sort: null, sortIndex: null })) });
    }
    api.paginationGoToPage(0);

    sortRef.current = { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_ORDER };
    api.applyColumnState({
      state: [{ colId: DEFAULT_SORT_FIELD, sort: DEFAULT_SORT_ORDER, sortIndex: 0 }],
      defaultState: { sort: null },
    });

    isResettingRef.current = false;

    resetFetchState();
    fetchAuditLogs(1);
  }, [fetchAuditLogs, resetFetchState]);

  const handleRefresh = useCallback(() => {
    lastFetchArgsRef.current = '';
    fetchAuditLogs(1);
  }, [fetchAuditLogs]);

  const gridStyle = useMemo(
    () =>
      ({
        width: '100%',
        '--ag-border-color': isDark ? '#252B32' : '#e0e0e0',
        ...containerStyle,
      }) as CSSProperties,
    [containerStyle, isDark]
  );

  if (hasLoadedOnce === false && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[220px]">
        <CtySpinner size="lg" />
      </div>
    );
  }

  if (hasLoadedOnce === false && error) {
    return <div className="p-3 text-danger">{t('Failed to load audit logs')}</div>;
  }

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <CtyButton className="rounded-md p-3 border-1" variant="bordered" size="sm" onPress={resetFilters}>
          {t('Reset Filters')}
        </CtyButton>
        <CtyButton className="rounded-md p-3 border-1" variant="bordered" size="sm" onPress={handleRefresh}>
          <span className="inline-flex items-center gap-1.5">
            <Icon icon="heroicons:arrow-path" width={16} height={16} />
            {t('Refresh')}
          </span>
        </CtyButton>
      </div>
      <div className={`ag-theme-alpine w-full relative doc-ag-grid-rows-clickable ${className ?? ''}`} style={gridStyle}>
        <style>{`
          .doc-ag-grid-rows-clickable .ag-row .ag-cell { cursor: pointer; }
          .doc-ag-grid-rows-clickable .ag-row.ag-row-selected .ag-cell {
            background-color: var(--ag-selected-row-background-color) !important;
          }
        `}</style>
        <AgGridReact<AuditLogItem>
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data.audit_id}
          headerHeight={40}
          rowHeight={48}
          domLayout="autoHeight"
          pagination
          paginationAutoPageSize={false}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={[10, 20, 50]}
          rowSelection="single"
          onPaginationChanged={onPaginationChanged}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          onSortChanged={onSortChanged}
          onFilterChanged={onFilterChanged}
          onCellClicked={onCellClicked}
        />
      </div>
      {selectedAuditId && (
        <AuditLogDetailDrawer
          isOpen={isDetailOpen}
          auditId={selectedAuditId}
          onClose={handleDetailClose}
          dateTimeFormat={resolvedDateTimeFormat}
        />
      )}
    </>
  );
}

export { AuditLogsAgGridWrapper, AuditTypeChipCellRenderer, AuditStatusChipCellRenderer };
